import type { AcceptRejectSharedDocumentsResponse, ApiError } from '@izimi/api';

import type { MessagesType, MessageType } from '../types';
import type DocumentEntity from './DocumentEntity';

const VAULT_SIZE_EXCEEDED_ERROR_MESSAGE: ApiError['userMessage'] =
  'client.technical.vaultlimitexceeded';

const documentsMessages = {
  trashDocuments({
    numberOfDocs,
    fileName,
  }: {
    numberOfDocs: number;
    fileName?: DocumentEntity['name'];
  }): MessagesType {
    let successMessage: MessageType['message'] = [
      'messages.documents.trashDocumentsSuccess',
      {
        fileName,
      },
    ];
    if (numberOfDocs > 1) {
      successMessage = [
        'messages.documents.trashDocumentMultipleSuccess',
        {
          numberOfDocs,
        },
      ];
    }

    return {
      successMessage: {
        message: successMessage,
        icon: 'trash-2',
      },
      errorMessage: {
        message: ['messages.documents.trashDocumentsFailed', { count: numberOfDocs }],
      },
    };
  },
  permanentlyDeleteDocuments({
    numberOfDocs,
    fileName,
  }: {
    numberOfDocs: number;
    fileName?: DocumentEntity['name'];
  }): MessagesType {
    let successMessage: MessageType['message'] = [
      'messages.documents.permanentlyDeleteDocumentsSuccess',
      {
        fileName,
      },
    ];
    if (numberOfDocs > 1) {
      successMessage = [
        'messages.documents.permanentlyDeleteDocumentsMultipleSuccess',
        {
          numberOfDocs,
        },
      ];
    }

    return {
      successMessage: {
        message: successMessage,
        icon: 'trash-2',
      },
      errorMessage: {
        message: ['messages.documents.permanentlyDeleteDocumentsFailed', { count: numberOfDocs }],
      },
    };
  },
  copyDocument(fileName: DocumentEntity['name']): MessagesType {
    return {
      successMessage: {
        message: [
          'messages.documents.copyDocumentSuccess',
          {
            fileName,
          },
        ],
        icon: 'copy',
      },
      errorMessage: {
        message: [
          'messages.documents.copyDocumentFailed',
          {
            fileName,
          },
        ],
      },
    };
  },
  copyMultipleDocuments(numberOfDocs: string): MessagesType {
    return {
      successMessage: {
        message: ['messages.documents.copyMultipleSuccess', { numberOfDocs }],
        icon: 'copy',
      },
      errorMessage: {
        message: ['messages.documents.copyMultipleFailed', { numberOfDocs }],
      },
    };
  },
  unTrashDocument(numberOfDocs: number): MessagesType {
    return {
      successMessage: {
        message: ['messages.documents.unTrashDocumentSuccess', { count: numberOfDocs }],
        icon: 'refresh-ccw',
      },
      errorMessage: {
        message: ['messages.documents.unTrashDocumentFailed', { count: numberOfDocs }],
      },
    };
  },
  moveDocumentsToTrash(numberOfDocs: number, fileName: string): MessagesType {
    return {
      successMessage: {
        message:
          numberOfDocs > 1
            ? [
                'messages.documents.moveDocumentsToTrashMultipleSuccess',
                {
                  numberOfDocs,
                },
              ]
            : [
                'messages.documents.moveDocumentsToTrashSuccess',
                {
                  fileName,
                },
              ],
        icon: 'trash-2',
      },
      errorMessage: {
        message: 'messages.documents.moveDocumentsToTrashFailed',
      },
    };
  },
  shareDocuments(numberOfDocs: number): MessagesType {
    return {
      successMessage: {
        message: ['messages.documents.shareDocumentsSuccess', { count: numberOfDocs }],
        icon: 'share-2',
      },
      errorMessage: {
        message: [
          `messages.documents.shareDocumentsFailed`,
          {
            numberOfDocs,
          },
        ],
      },
    };
  },
  getDocuments(): MessagesType {
    return {
      errorMessage: {
        message: 'messages.documents.getDocumentsFailed',
      },
    };
  },
  getDocument(fileName: string) {
    return {
      errorMessage: {
        message: [
          'messages.documents.getDocumentFailed',
          {
            fileName,
          },
        ],
      },
    };
  },
  updateDocumentCategories(): MessagesType {
    return {
      errorMessage: {
        message: 'messages.documents.updateDocumentCategoriesFailed',
      },
    };
  },
  updateDocumentLabels(): MessagesType {
    return {
      errorMessage: {
        message: 'messages.documents.updateDocumentLabelsFailed',
      },
    };
  },
  updateDocumentFileName(): MessagesType {
    return {
      errorMessage: {
        message: 'messages.documents.updateDocumentFileNameFailed',
      },
    };
  },
  previewDocument(fileName: string): MessagesType {
    return {
      errorMessage: {
        message: [
          'messages.documents.previewDocumentFailed',
          {
            fileName,
          },
        ],
      },
    };
  },
  unshareDocument(fileName: string): MessagesType {
    return {
      errorMessage: {
        message: [
          'messages.documents.unshareDocumentFailed',
          {
            fileName,
          },
        ],
      },
    };
  },
  acceptDocuments(count: number): MessagesType {
    return {
      successMessage: ({
        acceptedDocuments,
        failedDocuments,
      }: AcceptRejectSharedDocumentsResponse) => {
        if (acceptedDocuments.length > 0 && failedDocuments.length > 0) {
          return {
            message: [
              'messages.documents.acceptDocumentsPartialSuccess',
              {
                accepted: acceptedDocuments.length,
                failed: failedDocuments.length,
              },
            ],
          };
        }

        if (failedDocuments.length > 0) {
          return {
            message: [
              'messages.documents.acceptDocumentsFailed',
              { count: failedDocuments.length },
            ],
          };
        }

        return {
          message: ['messages.documents.acceptDocumentsSuccess', { count }],
        };
      },
      errorMessage: async (errorResponse) => {
        const defaultMessage = {
          message: ['messages.documents.acceptDocumentsFailed', { count }],
        } satisfies MessageType;

        try {
          const error = await errorResponse.json();

          if (error?.userMessage === VAULT_SIZE_EXCEEDED_ERROR_MESSAGE) {
            return {
              message: 'messages.documents.copyDocumentFailedVaultFull',
            };
          }
        } catch {
          return defaultMessage;
        }

        return defaultMessage;
      },
    };
  },
  rejectDocuments(count: number): MessagesType {
    return {
      successMessage: {
        message: ['messages.documents.rejectDocumentsSuccess', { count }],
      },
      errorMessage: {
        message: ['messages.documents.rejectDocumentsFailed', { count }],
      },
    };
  },
  handleDocuments(numberOfAcceptedDocs: number): MessagesType {
    return {
      successMessage: {
        message: ['messages.documents.acceptDocumentsSuccess', { count: numberOfAcceptedDocs }],
      },
      errorMessage: {
        message: 'messages.documents.handleDocumentsFailed',
      },
    };
  },
  getDocumentLabels(): MessagesType {
    return {
      errorMessage: {
        message: 'messages.documents.getDocumentLabelsFailed',
      },
    };
  },
};

export default documentsMessages;
